import {Col, Row} from "../../components/Flex/Flex.tsx";
import {Paper, Select, Button} from "@mantine/core";
import {useEntityTypes, useEntityHistory} from "../../utils/api.ts";
import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import { Link as IconLink, ArrowLeft as IconArrowLeft, ArrowRight as IconArrowRight } from "@phosphor-icons/react";
import CGT from "../../utils/CGT.ts";
import {useSearchParams} from "react-router";
import MessageContext from "../../context/MessageContext.tsx";

// History snapshot type - includes entity data plus metadata
type EntitySnapshot<T> = T & {
  uid: string;
  timestamp: number;
  hash: string;
};

type ShipSnapshot = EntitySnapshot<ShipType>;
type StationSnapshot = EntitySnapshot<StationType>;

export default function EntityHistoryCompare({type}: { type: 'ships' | 'stations' }) {

  const {[type]: currentList} = useEntityTypes([type], true);
  const {data: allHistory = [], isPending, isError} = useEntityHistory(type);

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [selectedSnapshot1, setSelectedSnapshot1] = useState<number | null>(null);
  const [selectedSnapshot2, setSelectedSnapshot2] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const {success, error} = useContext(MessageContext);

  // Read URL parameters on mount
  useEffect(() => {
    const entity = searchParams.get('entity');
    const snap1 = searchParams.get('p');
    const snap2 = searchParams.get('n');

    if (entity) setSelectedEntity(entity);
    if (snap1) setSelectedSnapshot1(parseInt(snap1));
    if (snap2) setSelectedSnapshot2(parseInt(snap2));

    setIsInitialized(true);
  }, []);

  // Update URL parameters when selections change
  useEffect(() => {
    if (!isInitialized) return;

    const params = new URLSearchParams();
    if (selectedEntity) params.set('entity', selectedEntity);
    if (selectedSnapshot1) params.set('p', selectedSnapshot1.toString());
    if (selectedSnapshot2) params.set('n', selectedSnapshot2.toString());

    setSearchParams(params, { replace: true });
  }, [selectedEntity, selectedSnapshot1, selectedSnapshot2, isInitialized]);

  // Filter history snapshots by selected entity - type as union of possible snapshots
  const entitySnapshots = useMemo(() => {
    if (!selectedEntity || !allHistory.length) return [];
    return (allHistory as Array<ShipSnapshot | StationSnapshot>)
      .filter((h: ShipSnapshot | StationSnapshot) => h.uid === selectedEntity)
      .sort((a: ShipSnapshot | StationSnapshot, b: ShipSnapshot | StationSnapshot) => b.timestamp - a.timestamp);
  }, [selectedEntity, allHistory]);

  // Auto-select snapshots when entity changes
  useEffect(() => {
    if (!isInitialized) return; // Wait for URL params to be processed

    if (entitySnapshots.length >= 2) {
      setSelectedSnapshot1(entitySnapshots[1].timestamp); // Previous
      setSelectedSnapshot2(entitySnapshots[0].timestamp); // Current (latest)
    } else if (entitySnapshots.length === 1) {
      setSelectedSnapshot1(entitySnapshots[0].timestamp);
      setSelectedSnapshot2(null);
    } else {
      setSelectedSnapshot1(null);
      setSelectedSnapshot2(null);
    }
  }, [entitySnapshots, isInitialized]);

  const [prev, next] = useMemo<[(ShipSnapshot | StationSnapshot) | undefined, (ShipSnapshot | StationSnapshot) | undefined]>(() => {
    if (!selectedSnapshot1 || !selectedSnapshot2 || entitySnapshots.length === 0) {
      return [undefined, undefined];
    }

    return [
      entitySnapshots.find((s: ShipSnapshot | StationSnapshot) => s.timestamp === selectedSnapshot1),
      entitySnapshots.find((s: ShipSnapshot | StationSnapshot) => s.timestamp === selectedSnapshot2),
    ]

  }, [selectedSnapshot1, selectedSnapshot2, entitySnapshots]);

  const navigate = useCallback((direction: 'next' | 'prev') => {
    setSelectedEntity(current => {
      const currentIndex = currentList.findIndex(opt => opt.id === current);
      if (currentIndex === -1) return current;

      let newIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;
      if (newIndex >= currentList.length) newIndex = 0; // Loop to first option
      if (newIndex < 0) newIndex = currentList.length - 1; // Loop to last option

      return currentList[newIndex].id;
    })
  }, [currentList]);

  const copyLinkToClipboard = useCallback(async () => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams();

    if (selectedEntity) params.set('entity', selectedEntity);
    if (selectedSnapshot1) params.set('p', selectedSnapshot1.toString());
    if (selectedSnapshot2) params.set('n', selectedSnapshot2.toString());

    url.search = params.toString();

    try {
      await navigator.clipboard.writeText(url.toString());
      success('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy link:', err);
      error('Failed to copy link to clipboard');
    }
  }, [selectedEntity, selectedSnapshot1, selectedSnapshot2]);

  const formatDate = (timestamp: number) => {
    return CGT.fromSeconds(timestamp).toFormat('yyyy-MM-dd') + ' / ' + CGT.fromSeconds(timestamp).toCGT(true);
  };

  const snapshotOptions = useMemo(() => {
    return entitySnapshots.map((s: ShipSnapshot | StationSnapshot, i: number) => ({
      label: formatDate(s.timestamp) + (i === 0 ? ' (Current)' : '') + (i === 1 ? ' (Previous)' : ''),
      value: s.timestamp
    }));
  }, [entitySnapshots]);

  if (isPending) return <div>Loading history...</div>;
  if (isError) return <div>Error loading history</div>;

  const title = type === 'ships' ? 'Ship Stat History' : 'Station Stat History';

  return (
    <div>
      <title>{title}</title>
      <Row align="center">
        <Col><h1>Compare entity history: <span className="capitalize">{type}</span></h1></Col>
        <Col grow={1}/>
        <Col>
          <Button
            leftSection={<IconLink size={16}/>}
            onClick={copyLinkToClipboard}
            disabled={!selectedEntity}
            variant="outline"
          >Copy Link</Button>
        </Col>
      </Row>
      <Paper className="mb-3">
        <Row>
          <Col>
            <h2 className="mt-0 mb-4">Select Entity</h2>
          </Col>
          <Col grow={1}/>
          <Col>
            <Button leftSection={<IconArrowLeft size={16}/>} onClick={() => navigate('prev')} disabled={selectedEntity === ''}/>
          </Col>
          <Col>
            <Button leftSection={<IconArrowRight size={16}/>} onClick={() => navigate('next')} disabled={selectedEntity === ''}/>
          </Col>
        </Row>
        <Select value={selectedEntity} onChange={(v) => setSelectedEntity(v || '')} searchable
                  data={currentList.map(e => ({value: e.id, label: e.name}))} placeholder="Select an entity"/>

        {entitySnapshots.length > 0 && (
          <div className="mt-4">
            <h3>Select Snapshots to Compare</h3>
            <Row>
              <Col grow={1}>
                <label className="block mb-2">First Snapshot (Previous)</label>
                <Select
                  value={selectedSnapshot1 ? String(selectedSnapshot1) : null}
                  onChange={v => setSelectedSnapshot1(v ? parseInt(v) : null)}
                  data={snapshotOptions.map(o => ({value: String(o.value), label: o.label}))}
                  placeholder="Select first snapshot"
                  className="w-full"
                />
              </Col>
              <Col grow={1}>
                <label className="block mb-2">Second Snapshot (Current)</label>
                <Select
                  value={selectedSnapshot2 ? String(selectedSnapshot2) : null}
                  onChange={v => setSelectedSnapshot2(v ? parseInt(v) : null)}
                  data={snapshotOptions.map(o => ({value: String(o.value), label: o.label}))}
                  placeholder="Select second snapshot"
                  className="w-full"
                />
              </Col>
            </Row>
          </div>
        )}

        {prev && next && (type === 'ships' ?
          <ShipDisplay entity={prev as ShipSnapshot} compare={next as ShipSnapshot}/> :
          <StationDisplay entity={prev as StationSnapshot} compare={next as StationSnapshot}/>)}

      </Paper>
    </div>
  )
}

const shipAttributes = [
  {label: "Hyper Speed", accessor: "speed.hyper", type: "number" as const},
  {label: "Sublight Speed", accessor: "speed.sublight", type: "number" as const},
  {label: "Planetary Speed", accessor: "speed.planetary", type: "number" as const},
  {label: "Manoeuvrability", accessor: "manoeuvrability", type: "number" as const},
  {label: "Sensors", accessor: "sensors", type: "number" as const},
  {label: "ECM", accessor: "ecm", type: "number" as const},
  {label: "Weight", accessor: "weight", type: "number" as const, flipped: true},
  {label: "Volume", accessor: "volume", type: "number" as const, flipped: true},
  {label: "Weight Capacity", accessor: "weightcap", type: "number" as const},
  {label: "Volume Capacity", accessor: "volumecap", type: "number" as const},
  {label: "Max Passengers", accessor: "maxpassengers", type: "number" as const},
  {label: "Length", accessor: "length", type: "number" as const, flipped: true},
  {label: "Hull", accessor: "hull", type: "number" as const},
  {label: "Shield", accessor: "shield", type: "number" as const},
  {label: "Ionic Resistance", accessor: "ionic", type: "number" as const},
  {label: "Armour", accessor: "armour", type: "number" as const},
  {label: "Slot Size", accessor: "slotsize", type: "number" as const},
  {label: "Repulsors", accessor: "repulsors", type: "boolean" as const},
  {label: "Hangar Bay", accessor: "hangarbay", type: "boolean" as const},
  {label: "Docking Bay", accessor: "dockingbay", type: "boolean" as const},
];
const stationAttributes = [
  {label: "Sensors", accessor: "sensors", type: "number" as const},
  {label: "ECM", accessor: "ecm", type: "number" as const},
  {label: "Weight", accessor: "weight", type: "number" as const, flipped: true},
  {label: "Volume", accessor: "volume", type: "number" as const, flipped: true},
  {label: "Weight Capacity", accessor: "weightcap", type: "number" as const},
  {label: "Volume Capacity", accessor: "volumecap", type: "number" as const},
  {label: "Max Passengers", accessor: "maxpassengers", type: "number" as const},
  {label: "Length", accessor: "length", type: "number" as const},
  {label: "Hull", accessor: "hull", type: "number" as const},
  {label: "Shield", accessor: "shield", type: "number" as const},
  {label: "Ionic Resistance", accessor: "ionic", type: "number" as const},
  {label: "Armour", accessor: "armour", type: "number" as const},
  {label: "Hangar Bay", accessor: "hangarbay", type: "boolean" as const},
  {label: "Docking Bay", accessor: "dockingbay", type: "boolean" as const},
]


function getValueByAccessor(obj: ShipSnapshot | StationSnapshot, accessor: string): string | number {
  // @ts-ignore
  return accessor.split('.').reduce((acc, key) => acc && acc[key], obj);
}


interface DiffRowProps {
  configuration: { label: string, accessor: string, type: 'number' | 'boolean', flipped?: boolean };
  prev: ShipSnapshot | StationSnapshot
  next: ShipSnapshot | StationSnapshot
  flipped?: boolean
}

const nf = new Intl.NumberFormat(undefined, {signDisplay: 'auto'});
const df = new Intl.NumberFormat(undefined, {signDisplay: 'always'});
const pc = new Intl.NumberFormat(undefined, {signDisplay: 'always', style: 'percent'})

function DiffRow({configuration, prev, next}: DiffRowProps) {
  let p = getValueByAccessor(prev, configuration.accessor);
  let n = getValueByAccessor(next, configuration.accessor);

  if (configuration.type === 'boolean') {
    let diff = (n ? 1 : 0) - (p ? 1 : 0);
    return (
      <tr>
        <th className="text-left">{configuration.label}</th>
        <td>{p ? 'Y' : 'N'}</td>
        <td className={diff < 0 ? 'text-red' : 'text-green'}>
          {diff > 0 && 'Added'}
          {diff < 0 && 'Removed'}
        </td>
        <td/>
        <td>{n ? 'Y' : 'N'}</td>
      </tr>
    )
  }

  if (typeof n !== 'number') {
    n = parseFloat(n)
  }
  if (typeof p !== 'number') {
    p = parseFloat(p)
  }

  const diff = n - p;
  const perc = diff / (p === 0 ? 1 : p);
  const color = (configuration.flipped
    ? diff >= 0
    : diff < 0)
    ? 'text-red'
    : 'text-green';

  return (
    <tr>
      <th className="text-left">{configuration.label}</th>
      <td>{nf.format(p)}</td>
      <td className={color}>{diff !== 0 ? df.format(diff) : ""}</td>
      <td className={color}>{diff !== 0 ? pc.format(perc) : ''}</td>
      <td>{nf.format(n)}</td>
    </tr>
  )
}

function ShipDisplay({entity, compare}: { entity: ShipSnapshot, compare: ShipSnapshot }) {

  return (
    <table className="w-full mt-4">
      <thead>
      <tr>
        <th></th>
        <th>Previous</th>
        <th>Change</th>
        <th/>
        <th>New</th>
      </tr>
      </thead>
      <tbody className="text-right">
      {shipAttributes.map(row => <DiffRow key={row.label} configuration={row} prev={entity} next={compare}/>)}
      </tbody>
      <WeaponsDiff prev={entity} next={compare}/>
      <MaterialsDiff prev={entity} next={compare}/>
    </table>
  )

}

function StationDisplay({entity, compare}: { entity: StationSnapshot, compare: StationSnapshot }) {
  return (
    <table className="w-full">
      <thead>
      <tr>
        <th></th>
        <th>Previous</th>
        <th>Change</th>
        <th/>
        <th>New</th>
      </tr>
      </thead>
      <tbody className="text-right">
      {stationAttributes.map(row => <DiffRow configuration={row} prev={entity} next={compare}/>)}
      </tbody>
    </table>
  )
}


type WeaponDiffData = {
  name: string;
  prev: number;
  next: number;
};

type WeaponsDiffProps = {
  prev: ShipSnapshot | StationSnapshot;
  next: ShipSnapshot | StationSnapshot;
};

function WeaponsDiff({prev, next}: WeaponsDiffProps) {
  const list: Record<string, WeaponDiffData> = {};

  prev.weapons.forEach((w: MountedWeapon) => {
    if (typeof list[w.id] === 'undefined') {
      list[w.id] = {name: w.name, prev: 0, next: 0};
    }
    list[w.id].prev = w.qty;
  });
  next.weapons.forEach((w: MountedWeapon) => {
    if (typeof list[w.id] === 'undefined') {
      list[w.id] = {name: w.name, prev: 0, next: 0};
    }
    list[w.id].next = w.qty;
  });

  return (
    <tbody className="text-right">
    <tr>
      <th colSpan={5} className="text-center">Weapons</th>
    </tr>
    {Object.values(list).map((row: WeaponDiffData) => <WeaponRow key={row.name} data={row}/>)}
    </tbody>
  )

}

function WeaponRow({data}: { data: WeaponDiffData }) {
  const diff = data.next - data.prev;
  return (
    <tr>
      <th className="text-left">{data.name}</th>
      <td>{nf.format(data.prev)}</td>
      <td className={diff < 0 ? 'text-red' : 'text-green'}>{diff !== 0 ? nf.format(diff) : ""}</td>
      <td/>
      <td>{nf.format(data.next)}</td>
    </tr>
  )
}


type MaterialDiffData = {
  id: string;
  name: string;
  prev: number;
  next: number;
};

type MaterialsDiffProps = {
  prev: ShipSnapshot | StationSnapshot;
  next: ShipSnapshot | StationSnapshot;
};

function MaterialsDiff({prev, next}: MaterialsDiffProps) {
  const list: Record<string, MaterialDiffData> = {};
  const {materials} = useEntityTypes(['materials']);

  Object.entries(prev.materials).forEach(([id, qty]: [string, number]) => {
    if (typeof list[id] === 'undefined') {
      const m = materials.find((m: MaterialType) => m.id === id);
      list[id] = {id, name: m?.name || id, prev: 0, next: 0};
    }
    list[id].prev = qty;
  })

  Object.entries(next.materials).forEach(([id, qty]: [string, number]) => {
    if (typeof list[id] === 'undefined') {
      const m = materials.find((m: MaterialType) => m.id === id);
      list[id] = {id, name: m?.name || id, prev: 0, next: 0};
    }
    list[id].next = qty;
  })

  return (
    <tbody className="text-right">
    <tr>
      <th colSpan={5} className="text-center">Materials</th>
    </tr>
    {Object.values(list).map((row: MaterialDiffData) => <MaterialRow key={row?.id} data={row}/>)}
    </tbody>
  )
}

function MaterialRow({data}: { data: MaterialDiffData }) {
  const diff = data.next - data.prev;
  const perc = data.prev > 0 ?  diff / data.prev : null;
  const color = (diff > 0) ? 'text-red' : 'text-green';
  return (
    <tr>
      <th className="text-left capitalize">{data.name}</th>
      <td>{nf.format(data.prev)}</td>
      <td className={color}>{diff !== 0 ? nf.format(diff) : ""}</td>
      <td className={color}>{diff !== 0 && perc !== null ? pc.format(perc) : ''}</td>
      <td>{nf.format(data.next)}</td>
    </tr>
  )
}
